import { useEffect } from 'react';
import Router from 'next/router';

const STYLE_ID = '__scroll_lock_global__';

const LOCK_PROPS: readonly string[] = [
	'overflow',
	'overflow-y',
	'overflow-x',
	'overscroll-behavior',
];
const IOS_PROPS: readonly string[] = ['position', 'top', 'width'];
const ALL_PROPS: readonly string[] = [...LOCK_PROPS, ...IOS_PROPS];

const LOCKED_VALUES: Record<string, string> = {
	overflow: 'hidden',
	'overflow-y': 'hidden',
	'overflow-x': 'hidden',
	'overscroll-behavior': 'contain',
	position: 'fixed',
	width: '100%',
};

type Snapshot = Record<string, { value: string; priority: string }>;

let lockCount = 0;
let snapshot: Snapshot | null = null;
let scrollYBefore = 0;
let navigating = false;
let routerHooked = false;

const isIOS = () =>
	typeof navigator !== 'undefined' && /iP(hone|ad|od)/.test(navigator.userAgent || '');

const takeSnapshot = (): Snapshot => {
	const { style } = document.body;
	const props = isIOS() ? ALL_PROPS : LOCK_PROPS;

	return props.reduce<Snapshot>((acc, prop) => {
		acc[prop] = {
			value: style.getPropertyValue(prop),
			priority: style.getPropertyPriority(prop),
		};
		return acc;
	}, {});
};

const applyLockStyles = () => {
	const body = document.body;

	LOCK_PROPS.forEach((prop) => {
		body.style.setProperty(prop, LOCKED_VALUES[prop], 'important');
	});

	if (!document.getElementById(STYLE_ID)) {
		const styleEl = document.createElement('style');
		styleEl.id = STYLE_ID;
		styleEl.textContent = `
      html { overflow: hidden !important; }
      body { overscroll-behavior: contain !important; }
    `;
		document.head.appendChild(styleEl);
	}

	if (isIOS()) {
		scrollYBefore = window.scrollY;
		body.style.setProperty('position', 'fixed', 'important');
		body.style.setProperty('top', `-${scrollYBefore}px`, 'important');
		body.style.setProperty('width', '100%', 'important');
	}
};

const removeLockStyles = () => {
	const body = document.body;
	const styleEl = document.getElementById(STYLE_ID);
	if (styleEl?.parentNode) styleEl.parentNode.removeChild(styleEl);

	const props = isIOS() ? ALL_PROPS : LOCK_PROPS;

	props.forEach((prop) => {
		const prev = snapshot?.[prop];
		const isPoisoned =
			!prev ||
			!prev.value ||
			prev.priority === 'important' ||
			prev.value === LOCKED_VALUES[prop] ||
			(prop === 'top' && prev.value.startsWith('-'));

		if (isPoisoned) body.style.removeProperty(prop);
		else body.style.setProperty(prop, prev.value, prev.priority);
	});
};

const hardCleanup = () => {
	if (typeof document === 'undefined') return;

	const body = document.body;
	const styleEl = document.getElementById(STYLE_ID);
	if (styleEl?.parentNode) styleEl.parentNode.removeChild(styleEl);

	ALL_PROPS.forEach((prop) => {
		if (body.style.getPropertyPriority(prop) === 'important') body.style.removeProperty(prop);
	});
};

const hookRouter = () => {
	if (routerHooked || typeof window === 'undefined') return;
	routerHooked = true;

	Router.events.on('routeChangeStart', () => {
		navigating = true;
	});

	const onRouteSettled = () => {
		navigating = false;

		if (lockCount > 0) {
			applyLockStyles();
			return;
		}

		snapshot = null;
		hardCleanup();
	};

	Router.events.on('routeChangeComplete', onRouteSettled);
	Router.events.on('routeChangeError', onRouteSettled);
};

const acquire = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') return;

	hookRouter();
	lockCount += 1;
	if (lockCount > 1) return;

	snapshot = takeSnapshot();
	applyLockStyles();
};

const release = () => {
	if (typeof window === 'undefined' || typeof document === 'undefined') return;
	if (lockCount === 0) return;

	lockCount -= 1;
	if (lockCount > 0) return;

	const shouldRestoreScroll = isIOS() && !navigating;
	const y = scrollYBefore;

	removeLockStyles();
	snapshot = null;
	scrollYBefore = 0;

	if (shouldRestoreScroll) window.scrollTo(0, y);
};

export const forceUnlockBodyScroll = () => {
	lockCount = 0;
	snapshot = null;
	scrollYBefore = 0;
	hardCleanup();
};

export const useLockBodyScroll = (enabled = true) => {
	useEffect(() => {
		if (!enabled) return;

		acquire();
		return release;
	}, [enabled]);
};
